// MATCHi-avräknade betalningar — ETT kriterium, alla ytor (C3/BES-004).
//
// PROBLEM: MATCHi-speglade bokningar betalas i MATCHi, inte i Boply. Pengarna når
// aldrig Boplys konton — men SIE-exporten och alla intäktsaggregat räknade dem som
// Boply-intäkt: 139 265 SEK felklassade till 1930 (Stripe/kortkonto) / 3010 (banintäkt),
// och därmed dubbelräknade mot MATCHi:s egna utbetalningsunderlag.
// Rotorsak: reportExport.ts METHOD_ACCOUNTS saknar "matchi" → tyst fallback `|| "1930"`.
//
// KRITERIET (Fas 0, empiriskt mot prod — audit-reports/c3-sie/fas0-rapport.md):
// betalningens METOD, aldrig bokningens ursprung.
//   payment_method='matchi' AND status='paid'  → 229 rader = exakt 139 265,00 SEK ✓
// `bookings.matchi_slot_id` (origin) vore FEL: det fångar 4 rader (1 613 SEK) som är
// MATCHi-origin men betalda I Boply (3× klippkort + 1× kort med äkta Stripe-PI) —
// riktiga Boply-pengar som inte får försvinna ur intäkten.
//
// SEMANTIK (BES-004-tillägg 2026-08-09, hybrid):
//   - SIE-export      → EXKLUDERA helt (falska verifikationer i Fortnox annars)
//   - Rapport/dashboard → SÄRREDOVISA (Boply-intäkt vs MATCHi-avräknat)

/**
 * AP Nacka:s `payment_methods`-rad för MATCHi. Org-specifik och additiv: UUID:t finns
 * bara i AP, så OR-villkoret kan aldrig ge en falsk träff i en annan org. Fångar den
 * enda rad där metodsträngen avviker (275 kr, payment_method='manual' + MATCHi-fk +
 * MATCHi-origin) — den når SIE via PaymentReconciliations `status IN ('paid','manual')`.
 * Får en framtida org MATCHi-integration: lägg dess id här, eller slå upp
 * payment_methods.key='matchi' per org.
 */
export const MATCHI_PAYMENT_METHOD_ID = "2c0d8945-ac54-477c-b1ed-01ff45fbd388";

/** Metodsträngen — org-oberoende (delas av alla orgar via payment_methods.key). */
export const MATCHI_PAYMENT_METHOD = "matchi";

/** Minsta form en betalrad behöver för att klassas. Alla ytor har minst `payment_method`. */
export interface MatchiPaymentMarker {
  payment_method?: string | null;
  payment_method_id?: string | null;
}

/**
 * True när betalningen togs emot av MATCHi (pengarna nådde aldrig Boply).
 * Enda stället kriteriet definieras — ändra här, aldrig per yta.
 */
export function isMatchiSettledPayment(p: MatchiPaymentMarker | null | undefined): boolean {
  if (!p) return false;
  return (
    p.payment_method === MATCHI_PAYMENT_METHOD ||
    p.payment_method_id === MATCHI_PAYMENT_METHOD_ID
  );
}

/** Bara Boply-betalda rader — för SIE och för "Boply-intäkt"-nyckeltal. */
export function excludeMatchiPayments<T extends MatchiPaymentMarker>(rows: readonly T[]): T[] {
  return rows.filter((r) => !isMatchiSettledPayment(r));
}

/**
 * Särredovisning: dela raderna i Boply-intäkt vs MATCHi-avräknat.
 * Rapportytor visar båda — totalen får aldrig blanda dem.
 */
export function splitMatchiPayments<T extends MatchiPaymentMarker>(
  rows: readonly T[],
): { boply: T[]; matchi: T[] } {
  const boply: T[] = [];
  const matchi: T[] = [];
  for (const r of rows) (isMatchiSettledPayment(r) ? matchi : boply).push(r);
  return { boply, matchi };
}
